import { FiberPrepOrder } from '@features/siteStatusV2/types';
import FiberPrepOrderState from '@features/siteStatusV2/constants/order/fiber-prep/FiberPrepOrderState';
import {
  ChatLeftDotsFill,
  EnvelopeAt,
  GeoAltFill,
  PersonFill,
  TelephoneFill
} from 'react-bootstrap-icons';
import { getI18n } from 'react-i18next';

import { TranslationNamespaces } from '../../../../../locales/constants/translationNamespaces.enum';
import { ConfigOrderDetails } from '../../order-details';
import { Reports } from '../../reports';

const createFiberPrepDisplayConfig = (order: FiberPrepOrder): ConfigOrderDetails => {
  const { t } = getI18n();

  const traduction = (s1: string, s2?: string) => {
    return s2
      ? t(s1, s2, { ns: TranslationNamespaces.SITE_STATUS })
      : t(s1, { ns: TranslationNamespaces.SITE_STATUS });
  };

  const getOrderState = (state: string): string => {
    const stateMapping: Record<string, string> = {
      [FiberPrepOrderState.RESERVE_OPTICAL_ROUTE_IN_PROGRESS]:
        'orderDetails.preRacc.orderState.inProgress',
      [FiberPrepOrderState.RESERVED]: 'orderDetails.preRacc.orderState.pendingConfirmation',
      [FiberPrepOrderState.INSTALLATION_OK]: 'orderDetails.preRacc.orderState.installationSuccess',
      [FiberPrepOrderState.INSTALLATION_KO]: 'orderDetails.preRacc.orderState.installationFailed',
      [FiberPrepOrderState.ERROR]: 'orderDetails.preRacc.orderState.emailExpired',
      [FiberPrepOrderState.RESERVED_OPTICAL_ROUTE_OK]: 'orderDetails.preRacc.orderState.inProgress'
    };

    return stateMapping[state] ? traduction(stateMapping[state]) : ' - ';
  };

  return {
    header: {
      name: order.name,
      headerBadges: [
        {
          color: 'grey',
          label: `${traduction('OrderList.orderDate', `Order Date`)} : ${order.createdDate}`,
          icon: { iconName: 'solid-clock' }
        },
        { color: 'blue', label: order.dspName ?? '' }
      ]
    },
    sections: [
      {
        isVisible: false,
        shouldExport: true,
        fields: [
          {
            title: traduction('orderDetails.generalInformations.referenceServiceTaken'),
            icon: 'rss',
            value: { label: order.name ?? '' }
          },
          {
            title: traduction('OrderList.orderDate'),
            icon: 'light-bulb',
            value: { label: order.createdDate ?? '' }
          },
          {
            title: traduction('buildingDetail.dsp'),
            icon: 'light-bulb',
            value: { label: order.dspName ?? '' }
          }
        ]
      },
      {
        title: traduction('orderDetails.orderTracking.title'),
        isVisible: true,
        shouldExport: true,
        fields: [
          {
            title: traduction('orderDetails.orderTracking.orderStatus'),
            icon: 'rss',
            value: {
              label: order.rawStatus ?? ''
            }
          },
          {
            title: traduction('orderDetails.orderTracking.title'),
            icon: 'light-bulb',
            value: { label: getOrderState(order.state) }
          }
        ]
      },
      {
        title: traduction('orderDetails.generalInformations.title'),
        isVisible: true,
        shouldExport: true,
        fields: [
          {
            title: traduction('orderDetails.generalInformations.name'),
            icon: <PersonFill />,
            value: {
              label: order.generalInformation?.name ?? ''
            }
          },
          {
            title: traduction('orderDetails.generalInformations.firstName'),
            icon: <PersonFill />,
            value: { label: order.generalInformation?.firstName ?? '' }
          },
          {
            title: traduction('orderDetails.generalInformations.email'),
            icon: <EnvelopeAt />,
            value: { label: order.generalInformation?.mail ?? '' }
          },
          {
            title: traduction('orderDetails.generalInformations.address'),
            icon: <GeoAltFill />,
            value: { label: order.generalInformation?.address ?? '' }
          },
          {
            title: traduction('orderDetails.generalInformations.phone'),
            icon: <TelephoneFill />,
            value: { label: order.generalInformation?.phone ?? '' }
          }
        ]
      },
      {
        title: traduction('orderDetails.appointment.title'),
        isVisible: true,
        fields: [
          {
            title: traduction('orderDetails.appointment.appointmentDate'),
            icon: 'solid-clock',
            value: {
              label: order.appointment?.date ?? ''
            }
          },
          {
            title: traduction('orderDetails.appointment.appointmentTimeSlot'),
            icon: 'solid-clock',
            value: {
              label: order.appointment?.slot ?? ''
            }
          }
        ]
      },
      {
        title: traduction('orderDetails.intervention.title'),
        isVisible: true,
        shouldExport: true,
        fields: [
          {
            title: traduction('orderDetails.intervention.referenceOT'),
            icon: 'code-slash',
            value: {
              label: order.intervention.reference ?? ''
            }
          },
          {
            title: traduction('orderDetails.intervention.interventionStatus'),
            icon: 'rss',
            value: {
              label: order.intervention.rawStatus ?? ''
            }
          },
          {
            title: traduction('orderDetails.intervention.interventionState'),
            icon: 'code-slash',
            value: {
              label: order.intervention.rawState ?? ''
            }
          },
          {
            title: traduction('orderDetails.intervention.failureReason'),
            icon: 'light-bulb',
            value: {
              label: order.intervention.failureReason ?? ''
            }
          },
          {
            title: traduction('orderDetails.intervention.fieldComment'),
            icon: <ChatLeftDotsFill />,
            value: {
              label: order.intervention.commentaryField ?? ''
            }
          }
        ]
      },
      {
        custom: <Reports refPrestationPrise={order.name ?? ''} />,
        isVisible: true,
        shouldExport: false
      }
    ]
  };
};

export default createFiberPrepDisplayConfig;
